import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MessagesSquare, Users, CheckCircle2, XCircle } from 'lucide-react';
import PushSetupCard from './PushSetupCard';
import LocationPermissionHint from './LocationPermissionHint';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const COVERAGE_ROLES = ['super_admin', 'admin', 'office_admin', 'dispatch', 'hr'];

function CoveragePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/push/admin/coverage`, auth()).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const missing = data.users.filter((u) => !u.devices);
  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5" data-testid="push-coverage-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-slate-900 font-bold flex items-center gap-2"><Users className="w-4 h-4 text-blue-700" /> Who has notifications on</h3>
        <span className="text-sm font-semibold text-emerald-700" data-testid="push-coverage-count">{data.with_push} / {data.total}</span>
      </div>
      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
        <div className="h-full bg-emerald-500" style={{ width: `${data.total ? Math.round((data.with_push / data.total) * 100) : 0}%` }} />
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 max-h-72 overflow-y-auto pr-1">
        {[...missing, ...data.users.filter((u) => u.devices)].map((u) => (
          <div key={u.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-200" data-testid={`push-coverage-${u.id}`}>
            <span className="text-slate-800 truncate">{u.name}<span className="text-slate-500 text-xs ml-1.5">{u.role}</span></span>
            {u.devices ? <span className="text-emerald-700 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {u.devices} device{u.devices !== 1 ? 's' : ''}</span>
              : <span className="text-red-700 text-xs inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> not on</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NotificationSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto" data-testid="notification-setup-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Notifications</h1>
        <p className="text-slate-500 text-sm mt-1">Dispatches, crew replies and job updates now come through 365Ops — no more texts.</p>
      </div>
      <PushSetupCard />
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-5" data-testid="location-setup-card">
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Location for clock-ins</h2>
        <p className="text-slate-500 text-sm mt-1 mb-3">When you acknowledge a foreman's clock-in (or clock in/out yourself) 365Ops tags your GPS location. Your phone decides whether it asks each time — here's how to allow it once.</p>
        <LocationPermissionHint alwaysShowSteps />
      </div>
      <button onClick={() => navigate('/dispatch-chat')}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 border border-slate-200 text-slate-800 font-medium px-4 py-3 rounded-xl text-sm transition-colors"
        data-testid="notif-open-chat-btn">
        <MessagesSquare className="w-4 h-4 text-blue-700" /> Open my dispatch chats
      </button>
      {COVERAGE_ROLES.includes(user?.role) && <CoveragePanel />}
    </div>
  );
}
